import { useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { Header } from '@/components/Header';
import { Footer } from '@/components/Footer';
import { ContactDialog } from '@/components/contact/ContactDialog';
import { Seo } from '@/components/seo/Seo';
import { INDEX_ROBOTS, NOINDEX_ROBOTS } from '@/lib/seo';
import { pricingPowerAdvisor, PricingPowerAdvisorLandingContent } from '@/content/pricingPowerAdvisor';

const LANDING_PATH = '/plugins/pricing-power-advisor';
const linkClass = 'font-medium text-primary underline underline-offset-4 hover:text-primary/80 focus-visible:rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring';

function PluginLayout({ children }: { children: ReactNode }) {
  return (
    <div className="flex min-h-screen flex-col bg-background text-foreground">
      <Header />
      <main id="main-content" className="flex-1">
        <div className="mx-auto w-full max-w-4xl px-5 py-12 sm:px-8 sm:py-16">{children}</div>
      </main>
      <Footer />
    </div>
  );
}

export default function PricingPowerAdvisorPage() {
  return (
    <div className="flex min-h-screen flex-col bg-background text-foreground">
      <Seo
        title={pricingPowerAdvisor.title}
        description={pricingPowerAdvisor.description}
        canonical={pricingPowerAdvisor.path}
        robots={INDEX_ROBOTS}
      />
      <Header />
      <PricingPowerAdvisorLandingContent />
      <Footer />
    </div>
  );
}

export function PricingPowerAdvisorSupportPage() {
  const [contactOpen, setContactOpen] = useState(false);

  return (
    <PluginLayout>
      <Seo
        title="Pricing Power Advisor Support | Applyt"
        description="Help with access, use, and troubleshooting for Pricing Power Advisor."
        robots={NOINDEX_ROBOTS}
      />
      <nav aria-label="Breadcrumb" className="text-sm text-muted-foreground">
        <Link className={linkClass} to={LANDING_PATH}>Pricing Power Advisor</Link> <span aria-hidden="true">/</span> <span aria-current="page">Support</span>
      </nav>
      <h1 className="mt-5 text-4xl font-semibold tracking-tight sm:text-5xl">Pricing Power Advisor support</h1>
      <p className="mt-5 max-w-2xl text-lg leading-8 text-muted-foreground">Help with access, getting started, and using the plugin in ChatGPT. It is currently available by invitation; public directory availability follows review and approval.</p>

      <div className="mt-10 space-y-10">
        <section aria-labelledby="support-install">
          <h2 id="support-install" className="text-2xl font-semibold">Get access and use the plugin</h2>
          <ol className="mt-4 list-decimal space-y-2 pl-6 leading-7 text-muted-foreground">
            <li>Open the invitation or plugin link you received in ChatGPT.</li>
            <li>Sign in with the ChatGPT account that has access and follow the prompts to install or enable it.</li>
            <li>Start a conversation and explain your business, current pricing, and the decision you are considering. Review the response and ask follow-up questions.</li>
          </ol>
          <p className="mt-3 leading-7 text-muted-foreground">If you do not have access yet, contact the person who shared the plugin with you. This support page is not an installation link.</p>
        </section>

        <section aria-labelledby="support-limits">
          <h2 id="support-limits" className="text-2xl font-semibold">What it can and cannot do</h2>
          <p className="mt-4 leading-7 text-muted-foreground">The plugin helps you reason through pricing options and plan a test. It cannot change your prices or connect to bank or customer data. You remain responsible for any business decision or action.</p>
          <p className="mt-3 leading-7 text-muted-foreground">Its output is decision support, not financial, legal, or accounting advice. Verify figures and assumptions independently and consult a qualified professional where appropriate.</p>
        </section>

        <section aria-labelledby="support-troubleshooting">
          <h2 id="support-troubleshooting" className="text-2xl font-semibold">Troubleshooting</h2>
          <ul className="mt-4 list-disc space-y-2 pl-6 leading-7 text-muted-foreground">
            <li>If the link does not open, check that you are signed in to the ChatGPT account with access and try the original link again.</li>
            <li>If you cannot find the plugin after installing it, reopen the link you used and check whether ChatGPT shows an install or enable step.</li>
            <li>If a response seems incomplete, add the missing business context and ask a narrower follow-up question.</li>
          </ul>
        </section>

        <section aria-labelledby="support-contact" className="rounded-2xl border border-border bg-card p-6 sm:p-8">
          <h2 id="support-contact" className="text-2xl font-semibold">Contact support</h2>
          <p className="mt-3 leading-7 text-muted-foreground">Tell us what you were trying to do, the steps you took, what happened, and any error text. Include the plugin link or invitation context if useful. Remove sensitive business or personal details before sending.</p>
          <button
            type="button"
            onClick={() => setContactOpen(true)}
            className="mt-6 inline-flex min-h-11 items-center rounded-lg bg-primary px-5 py-2 font-medium text-primary-foreground transition-colors hover:bg-primary/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
          >
            Contact support
          </button>
        </section>
      </div>

      <p className="mt-10 text-sm leading-7 text-muted-foreground">Read the plugin <Link className={linkClass} to={`${LANDING_PATH}/privacy`}>Privacy Policy</Link> and <Link className={linkClass} to={`${LANDING_PATH}/terms`}>Terms of Service</Link>, or <Link className={linkClass} to={LANDING_PATH}>return to Pricing Power Advisor</Link>.</p>
      <ContactDialog
        open={contactOpen}
        onOpenChange={setContactOpen}
        title="Pricing Power Advisor support"
        description="Describe your access or usage issue and we will get back to you."
        defaultSubject="Pricing Power Advisor support"
        context="Pricing Power Advisor plugin support page"
      />
    </PluginLayout>
  );
}
